


/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import {
    CollaboratingGroupContext,
    CrfForm,
    FormBreadcrumbs,
    InstitutionContext,
    DatePicker,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    ToggleButtonGroup,
    CrfCondition,
    ICrfConditionParameters,
    Select,
    FormLookupsContext,
    GroupedFields,
    Text,
    GroupedField,
    AuthenticatedUserContext,
    FormContext,
    IFormContext,
    IFormLabel,
    FormErrorDisplayMode
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { useContext, useMemo } from 'react';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../../api/dtos';
import { LinearProgress, Typography, Alert, AlertTitle, styled, Box, Paper, lighten, Button, TextField, Grid2 } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useInvestigatorLookup, { INVESTIGATOR_LOOKUP_OTHER } from '../../../hooks/lookups/useInvestigatorLookup';
import { ConditionMode, useScopedField, ValidateOn } from '@ngt/forms-core';
import useConsentVersionLookup, { PICF_VERSION_OTHER_ID } from '../../../hooks/lookups/useConsentVersionLookup';
import { AppSettings } from '../../../AppSettings';
import EConsentContext, { EConsentUserType } from '../../../contexts/EConsentContext';
import { IConsentForm } from '../../../api/dtos';
import EConsentForm from '../EConsentForm';
import InputDisplay from '../inputs/InputDisplay';
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-duotone-svg-icons';
import SelectWithTooltip from '../inputs/SelectWithTooltip';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const BoldParagraph = styled(Typography)(({theme}) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    fontWeight: 'bold',

    [theme.breakpoints.down('sm')]: {
        width: '80%'
    }
}))

const Paragraph = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(1.5),
}))

const StyledWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0),

    [theme.breakpoints.up('md')]: {
        maxWidth: 1000,
        margin: 'auto',
        padding: theme.spacing(3),
    }
}))

const StyledHeader = styled(Typography)(({ theme }) => ({
    paddingBottom: theme.spacing(1.5),
    textAlign: 'center',
    fontSize: '1.25rem',
    color: theme.palette.primary.main,

    [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
        width: '80%'
    },
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3, 3, 3, 3),

    [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(5, 2, 5, 2),
        border: 'none',
        boxShadow: 'none'
    }
}))

const TrialInfo = styled(Alert)(({ theme }) => ({
    background: lighten(theme.palette.primary.main, 0.8),
    marginBottom: '1.5rem',
    padding: '1.5rem',
    
    '& *': {
        fontSize: '1rem'
    },
    '& > div': {
        padding: 0
    },
    '& > div > div': {
        marginBottom: '1rem'
    },
    '& > div > :last-of-type': {
        marginBottom: 0
    }
}))

const ChecklistContainer = styled(Box)(({ theme }) => ({
    marginLeft: '2rem',
    marginRight: '2rem',

    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        marginRight: 0,
    }
}))

const Checklist = styled(Box)(() => ({
    display: 'flex'
}))

const ChecklistIcon = styled(Box)(({ theme }) => ({
    color: theme.palette.primary.light,
    marginRight: '1rem',
    fontSize: '1rem'
}))

const PicfButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1)
}))


/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */


//const standardConsent = ({ formState }: ICrfConditionParameters<Dtos.TissueBankingConsent>) => formState?.value?.consentTypeId === Dtos.ConsentTypeEnum.Standard;
const patientSigned = ({ formState }: ICrfConditionParameters<Dtos.TissueBankingConsent>) => formState?.value?.patientSignedConsent === Dtos.LookupYesNoType.Yes;
const investigatorSigned = ({ formState }: ICrfConditionParameters<Dtos.TissueBankingConsent>) => formState?.value?.investigatorSignedConsent === Dtos.LookupYesNoType.Yes;
const picfVersionOther = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.picfId === PICF_VERSION_OTHER_ID;
const investigatorOther = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.investigator === INVESTIGATOR_LOOKUP_OTHER;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

interface ITissueBankingConsentProps { }

interface PicfVersionUpdaterProps {
    picfVersion: string | undefined;
}

const PicfVersionField: React.FunctionComponent = () => {
    const { data: patient } = React.useContext(PatientContext);

    const {
        state: {
            value: consentTypeId
        }
    } = useScopedField<Dtos.ConsentTypeEnum>(`consentTypeId`, { value: true }, false, false);
    
    const {data: picfLookup, loading: picfVersionsLoading } = useConsentVersionLookup('picfId', (patient as Dtos.Patient)?.institutionCode, 'Tissue');

    const [picfVersion, setPicfVersion] = React.useState<string|undefined>(undefined);

    const onPicfVersionChange = React.useCallback((picfId: number) => {
        const picfVersion = picfLookup?.items?.find(x => x.id === picfId)?.value;

        setPicfVersion(picfVersion);
    }, [picfLookup, setPicfVersion])

    const picfVersions = useMemo(() => {
        return [picfLookup];
    }, [picfLookup])

    const {
        state: {
            value: picfId
        }
    } = useScopedField<string>(`picfId`, { value: true }, false, false);

    const picfValue = React.useMemo(() => {
        return picfLookup?.items?.find(x => x.id == picfId)?.value
    }, [picfLookup, picfId]);

    return (
        <>
            <FormLookupsContext.Provider value={picfVersions}>
            {
                picfVersionsLoading ? 
                    <LinearProgress color='primary' /> : 
                    <GroupedFields>
                        <GroupedField
                            propertyPath="picfId"
                            component={SelectWithTooltip}
                            onChange={(event: any) => onPicfVersionChange(event.target.value)}
                            disabled={consentTypeId === Dtos.ConsentTypeEnum.Remote}
                            FieldGridProps={{
                                size: {
                                    xl: 3,
                                    lg: 3,
                                    md: 5,
                                    sm: 12,
                                    xs: 12
                                },
                            }}
                            tooltipProps={{
                                arrow: true,
                                placement: "right",
                                title: picfValue
                            }}
                        />
                        <CrfCondition
                            mode={ConditionMode.Show}
                            condition={picfVersionOther}
                            subscription={{ value: true }}
                        >
                            <GroupedField
                                propertyPath="picfVersionSpecify"
                                component={Text}
                            />
                        </CrfCondition>
                    </GroupedFields>
            }
            </FormLookupsContext.Provider>

            <PicfVersionUpdater picfVersion={picfVersion} />
        </>
    );
}

const PicfVersionUpdater: React.FunctionComponent<PicfVersionUpdaterProps> = ({
    picfVersion
}) => {
    const {
        context: {
            setValue: setPicfVersion
        }
    } = useScopedField<string>(`picfVersion`,  { value: true }, false, false);

    React.useEffect(() => {
        if (picfVersion)
        {
            setPicfVersion(picfVersion);
        }
    }, [picfVersion, setPicfVersion]);

    return (
        null
    );
}

const EConsentNote: React.FunctionComponent = () => {
    const {
        state: {
            value: consentType
        }
    } = useScopedField<Dtos.ConsentTypeEnum>(`consentTypeId`, { value: true }, false, false);

    return (
        consentType === Dtos.ConsentTypeEnum.Remote ? 
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} fixedWidth />}
                severity="info"
            >
                <AlertTitle>
                    Note
                </AlertTitle>
                <Typography>
                    The rest of the form will be completed after the patient and the investigator have completed the eConsent.
                </Typography>
            </Alert>
            :
            null
    );
}


const PatientSigningUpdater: React.FunctionComponent = () => {
    const { userType } = React.useContext(EConsentContext);

    const {
        context: {
            setValue: setPatientSigning
        }
    } = useScopedField<boolean>(`patientSigning`, {value: true}, false, false);

    const {
        context: {
            setValue: setPatientSignedConsent
        }
    } = useScopedField<number>(`patientSignedConsent`, {value: true}, false, false);

    const {
        state: {
            value: patientSignedConsent
        }
    } = useScopedField<number>(`patientSignedConsent`, {value: true}, false, false);

    React.useEffect(() => {
        if(userType === EConsentUserType.Participant && !patientSignedConsent)
        {
            setPatientSigning(true);
            setPatientSignedConsent(Dtos.LookupYesNoType.Yes);
        }

    }, [patientSignedConsent, setPatientSigning, setPatientSignedConsent, userType]);

    return (
        null
    );
}

const InvestigatorSigningUpdater: React.FunctionComponent = () => {
    const { userType } = React.useContext(EConsentContext);

    const { data: authUser } = useContext(AuthenticatedUserContext);

    const {
        context: {
            setValue: setInvestigatorSigning
        }
    } = useScopedField<boolean>(`investigatorSigning`, {value: true}, false, false);

    const {
        context: {
            setValue: setInvestigatorSignedConsent
        }
    } = useScopedField<number>(`investigatorSignedConsent`, {value: true}, false, false);

    const {
        context: {
            setValue: setInvestigator
        }
    } = useScopedField<string>(`investigator`, {value: true}, false, false);

    const {
        state: {
            value: investigatorSignedConsent
        }
    } = useScopedField<number>(`investigatorSignedConsent`, {value: true}, false, false);

    React.useEffect(() => {
        if(userType === EConsentUserType.Investigator && !investigatorSignedConsent)
        {
            setInvestigatorSigning(true);
            setInvestigatorSignedConsent(Dtos.LookupYesNoType.Yes);
            setInvestigator(authUser?.userName, true, true, true, false);
        }

    }, [investigatorSignedConsent, setInvestigatorSigning, setInvestigatorSignedConsent, setInvestigator, authUser, userType]);

    return (
        null
    );
}

const consentLookups: Dtos.ILookup[] = [
    {
        propertyName: 'particpantAgreedToSampleStorageAndUse',
        items: [
            {
                id: Dtos.LookupYesNoType.Yes,
                value: 'I agree'
            },
            {
                id: Dtos.LookupYesNoType.No,
                value: 'I do not agree'
            }
        ]
    },
    {
        propertyName: 'particpantAgreedToImageStorageAndUse',
        items: [
            {
                id: Dtos.LookupYesNoType.Yes,
                value: 'I agree'
            },
            {
                id: Dtos.LookupYesNoType.No,
                value: 'I do not agree'
            }
        ]
    }
];

const consentLabels: IFormLabel[] = [
    {
        propertyPath: 'particpantAgreedToSampleStorageAndUse',
        detailedLabel: undefined,
        label: 'Do you agree to the storage of your tumour and blood samples collected as part of PROSPECTIVE for future unspecified research?'
    },
    {
        propertyPath: 'particpantAgreedToImageStorageAndUse',
        detailedLabel: undefined,
        label: 'Do you agree to the storage of your images collected as part of PROSPECTIVE for future unspecified research?'
    },
    {
        propertyPath: 'patientName',
        detailedLabel: undefined,
        label: 'Name'
    },
    {
        propertyPath: 'datePatientSignedConsent',
        detailedLabel: undefined,
        label: 'Date'
    },
    {
        propertyPath: 'investigatorName',
        detailedLabel: undefined,
        label: 'Name'
    },
    {
        propertyPath: 'dateInvestigatorSignedConsent',
        detailedLabel: undefined,
        label: 'Date'
    }
];

const TissueBankingConsentEConsentForm: React.FunctionComponent = () => {
    const { investigators, userType } = React.useContext(EConsentContext);
    const { data: consentForm } = useContext(FormContext) as unknown as IFormContext<IConsentForm>;

    const investigatorName = React.useMemo(() => {
        if(investigators && consentForm?.consentEvent?.investigator)
        {
            return investigators[consentForm.consentEvent.investigator]
        }

        return null;
    }, [investigators, consentForm]);

    return (
        <>
            <EConsentForm
                disableHeading
                disablePaper
                labels={consentLabels}
                lookups={consentLookups}
                validateOn={ValidateOn.onChange}
                debug={AppSettings.debug}
                hideSubmit
                submitAndReturnButtonText="Submit"
            >
                <PatientSigningUpdater />
                <InvestigatorSigningUpdater />
                <GroupedFields
                    ContainerProps={{
                        borderBottom: 'none !important',
                        borderTop: 'none !important'
                    }}
                >
                    <BoldParagraph>
                        Consent to take part in the optional parts of this trial 
                    </BoldParagraph>

                    <GroupedField
                        propertyPath="particpantAgreedToSampleStorageAndUse"
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 5,
                                lg: 4,
                                xl: 4
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={userType === EConsentUserType.Investigator}
                    />

                    <GroupedField
                        propertyPath="particpantAgreedToImageStorageAndUse"
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 5,
                                lg: 4,
                                xl: 4
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={userType === EConsentUserType.Investigator}
                    />
                    
                    <BoldParagraph>
                        Person taking part in the trial 
                    </BoldParagraph>
                    
                    <GroupedField
                        propertyPath="patientName"
                        component={InputDisplay}
                        valueConvert={(value: string, _lookups: any) => <TextField value={value?.toUpperCase()} disabled fullWidth/>}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 6,
                                lg: 6,
                                xl: 6
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                    />

                    <GroupedField
                        propertyPath="datePatientSignedConsent"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 6,
                                lg: 6,
                                xl: 6
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={userType === EConsentUserType.Investigator}
                    />

                    {
                        userType === EConsentUserType.Investigator && <>
                            <BoldParagraph>
                                Person conducting the informed consent discussion (Principal Investigator or Senior Researcher)
                            </BoldParagraph>

                            <ChecklistContainer>
                                <Checklist>
                                    <ChecklistIcon><FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" /></ChecklistIcon>
                                    <Paragraph>
                                        I have explained the trial, its procedures and risks to the participant and I believe they have understood that explanation. 
                                    </Paragraph>
                                </Checklist>
                            </ChecklistContainer>

                            <GroupedField
                                propertyPath="investigatorName"
                                component={InputDisplay}
                                valueConvert={(_value: string, _lookups: any) => <TextField value={investigatorName?.toUpperCase()} disabled fullWidth/>}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                        xl: 6
                                    }
                                }}
                                ContainerGridProps={{
                                    sx: {
                                        paddingBottom: 1.5,
                                        paddingTop: 1.5
                                    }
                                }}
                            />

                            <GroupedField
                                propertyPath="dateInvestigatorSignedConsent"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                        xl: 6
                                    }
                                }}
                                ContainerGridProps={{
                                    sx: {
                                        paddingBottom: 1.5,
                                        paddingTop: 1.5
                                    }
                                }}
                            />
                        </>
                    }
                </GroupedFields>
            </EConsentForm>
        </>
    );
}

const TissueBankingConsentEConsent: React.FunctionComponent = () => {
    const { userType, principalInvestigator } = React.useContext(EConsentContext);
    const { data: consentForm } = React.useContext(FormContext) as unknown as IFormContext<IConsentForm>;
    const { data: institution } = React.useContext(InstitutionContext);

    const tissueBankingPicfTypeId = 2;

    const picfsDownloadLink = `/opms/picf/download/${institution?.code}/${consentForm?.picfId}/${tissueBankingPicfTypeId}`;

    if(!consentForm || !userType || userType === EConsentUserType.Unknown) {
        return <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                        <AlertTitle>
                            Consent Form Not Loaded
                        </AlertTitle>
                        An error occured while loading the consent form. Please report the issue to your treatment team.
                    </Alert>
                </StyledPaper>
            </StyledWrapper>
        </>
    }

    if(userType === EConsentUserType.Investigator && !consentForm.patientSignedConsent) {
        return <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                        <AlertTitle>
                            Consent Form Not Available
                        </AlertTitle>
                        The consent form is still awaiting for participant's signature.
                    </Alert>
                </StyledPaper>
            </StyledWrapper>
        </>
    }

    return (
        <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <StyledHeader>
                        <strong>{consentForm?.formDefinition?.name?.toUpperCase()}</strong>
                    </StyledHeader>
                    <TrialInfo
                        icon={false}
                    >
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Short Name of Trial</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    BCT 2401 PROSPECTIVE
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Full Name of Trial </strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    A two-arm, non-randomised, prospective, multicentre study using magnetic resonance imaging (MRI) findings and pathology features to select patients with early breast cancer for omission of post-operative radiotherapy 
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Principal Investigator</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    {principalInvestigator}
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Trial sponsor</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    Breast Cancer Trials 
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Site Name </strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    {institution?.name}
                                </Grid2>
                        </Grid2>
                    </TrialInfo>
                    <Paragraph>
                        This is <u>optional</u>, meaning that you must complete the form at the end of this information, but you can take part in PROSPECTIVE without having to donate your tumour and blood samples or images (scans) for banking for future unspecified research. 
                    </Paragraph>
                    <Paragraph>
                        This separate information sheet explains the optional tumour, blood and image banking part of the PROSPECTIVE trial. This for the tumour and blood samples and images (scans) collected whilst you are taking part in PROSPECTIVE and are left over after all the testing is done for the trial. 
                    </Paragraph>
                    <Paragraph>
                        Please click the following link to view the full Tissue Banking and Image Banking for Future Unspecified Research Participant Information and Consent Form. Please read this information and feel free to ask any questions. You can also talk to someone you trust, like a family member, mob, friend, or your doctor. You can take some time to make up your mind. You get to decide if this trial is right for you.  
                    </Paragraph>
                    <Paragraph>
                        All the information about how we will use your personal and health information that you read in the main consent form also applies to this information sheet and consent form. 
                    </Paragraph>


                    {
                        consentForm?.picfVersion && <Box style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                            <PicfButton
                                color="primary"
                                variant="contained"
                                //@ts-expect-error
                                component="a"
                                href={picfsDownloadLink} 
                                target="_blank" 
                                rel="noopener" 
                            >
                                {consentForm?.picfVersion}
                            </PicfButton>
                        </Box>
                    }

                    <br />

                    <Paragraph>
                        By signing this consent form, I acknowledge that: 
                    </Paragraph>

                    <ChecklistContainer>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I have read the attached Participant Information Sheet or someone has read it to me in a language that I understand. 
                            </Paragraph>
                        </Checklist>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I understand the purposes, procedures and risks of the research described in the trial. 
                            </Paragraph>
                        </Checklist>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I have discussed my participation in this trial with the member of the trial team named below. I have had the opportunity to ask questions and I am satisfied with the answers I have received. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I have been informed about the possible risks of taking part in this trial. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I consent to my doctors, other health professionals, hospitals or laboratories outside this institution releasing information concerning my condition and treatment which is needed for this trial and understand that such information will remain confidential.  
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I completed the selection options below regarding participation in this optional component of PROSPECTIVE, as described in the attached Participant Information Sheet. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I understand that my participation is voluntary and that I am free to withdraw at any time during the trial without affecting my future health care. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I understand that I will be given a signed copy of this document to keep. 
                            </Paragraph>
                        </Checklist>
                    </ChecklistContainer>
                    <TissueBankingConsentEConsentForm />
                </StyledPaper>
            </StyledWrapper>
        </>
    )
}

const TissueBankingConsentFields: React.FunctionComponent = () => {
    const { data: institution } = React.useContext(InstitutionContext);
    const { data: investigatorLookup, loading: investigatorsLoading } = useInvestigatorLookup('investigator', institution?.code);

    const investigators = useMemo(() => [investigatorLookup], [investigatorLookup]);

    const { state: { value: consentTypeId } } = useScopedField<Dtos.ConsentTypeEnum>('consentTypeId', { value: true}, false, false);

    const disabled = consentTypeId !== Dtos.ConsentTypeEnum.Standard


    return (
        <>
            <GroupedField
                propertyPath="patientSignedConsent"
                component={ToggleButtonGroup}
                disabled={disabled}
            />

            <CrfCondition
                mode={ConditionMode.Enable}
                condition={patientSigned}
                subscription={{ value: true }}
            >
                <PicfVersionField />

                <GroupedField
                    propertyPath="datePatientSignedConsent"
                    component={DatePicker}
                    disabled={disabled}
                />
                <GroupedField
                    propertyPath="particpantAgreedToSampleStorageAndUse"
                    component={ToggleButtonGroup}
                    disabled={disabled}
                />
                <GroupedField
                    propertyPath="particpantAgreedToImageStorageAndUse"
                    component={ToggleButtonGroup}
                    disabled={disabled}
                />
                <GroupedField
                    propertyPath="investigatorSignedConsent"
                    component={ToggleButtonGroup}
                    disabled={disabled}
                />
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={investigatorSigned}
                    subscription={{ value: true }}
                >
                    <GroupedField
                        propertyPath="dateInvestigatorSignedConsent"
                        component={DatePicker}
                        disabled={disabled}
                    />
                    <FormLookupsContext.Provider value={investigators}>
                    {
                        investigatorsLoading ? 
                        <LinearProgress color='primary' /> : 
                            <GroupedFields>
                                <GroupedField 
                                    propertyPath="investigator"
                                    component={Select}
                                    disabled={disabled}
                                />
                                <CrfCondition
                                    mode={ConditionMode.Show}
                                    condition={investigatorOther}
                                    subscription={{ value: true }}
                                >
                                    <GroupedField
                                        propertyPath="investigatorSpecify"
                                        component={Text}
                                        disabled={disabled}
                                    />
                                </CrfCondition>
                            </GroupedFields>
                    }    
                    </FormLookupsContext.Provider>
                </CrfCondition>
            </CrfCondition>
        </>
    )
}

const consentTypeLookup: Dtos.ILookup[] = [
    {
        propertyName: 'consentTypeId',
        items:[
            {
                id: Dtos.ConsentTypeEnum.Remote,
                value: 'eConsent'
            },
            {
                id: Dtos.ConsentTypeEnum.Standard,
                value: 'Standard'
            }
        ]
    }
];

const TissueBankingConsentForm: React.FunctionComponent<ITissueBankingConsentProps> = () => {

    const { data: masterGroup } = React.useContext(MasterGroupContext);
    const { data: collaboratingGroup } = React.useContext(CollaboratingGroupContext);
    const { data: institution } = React.useContext(InstitutionContext);
    const { data: patient } = React.useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const { data: form } = React.useContext(FormContext);
    
    const hasChanged = React.useMemo(() => {
        return form?.enteredDate !== form?.modifiedDate;
    }, [form]);

    const isEConsent = React.useMemo(() => {
        return (form as Dtos.TissueBankingConsent)?.consentTypeId === Dtos.ConsentTypeEnum.Remote
    }, [form]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings?.debug}
                errorMode={isEConsent ? 
                    FormErrorDisplayMode.AlwaysHideErrors :
                    hasChanged ? 
                        FormErrorDisplayMode.AlwaysShowAllErrors : 
                        FormErrorDisplayMode.AutoShowAllErrors
                }
            >
                <Typography sx={{px: 2, py: 3}}>
                    Main Consent must be completed prior to registration. Tissue Banking and Image Consent, and Services Australia Consent (AUS sites only) must be completed prior to allocation.
                </Typography>
                <FormLookupsContext.Provider value={consentTypeLookup}>
                    <GroupedField
                        propertyPath="consentTypeId"
                        component={ToggleButtonGroup}
                        LabelProps={{
                            label: <>Consent Type</>
                        }}
                        disabled
                    />
                </FormLookupsContext.Provider>

                <EConsentNote />

                <TissueBankingConsentFields />
            </CrfForm>
        </PageLayout>
    );
}


const TissueBankingConsent: React.FunctionComponent<ITissueBankingConsentProps> = () => {
    const { userType } = React.useContext(EConsentContext);

    return (
        <>
            {
                userType ? 
                    <TissueBankingConsentEConsent /> :
                    <TissueBankingConsentForm />
            }
        </>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default TissueBankingConsent;
